import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import type { ReactNode } from "react";
import { cloneElement, useEffect, useMemo, useRef, useState } from "react";

import type { CommunityFeedItemTypeDto, MassPostReachDto, PostReachDto } from "@/api/types";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { ArchiveIcon } from "@/components/Icons/ArchiveIcon";
import { Building01Icon } from "@/components/Icons/Building01Icon";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { InfoCircleIcon } from "@/components/Icons/InfoCircleIcon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { NoData } from "@/components/NoData/NoData";
import { ProgressBar } from "@/components/ProgressBar/ProgressBar";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import type { MessageRouteParams, MessageRouteStates } from "@/helpers/community-feed";
import { twResolve } from "@/helpers/tw-resolve";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePrompt } from "@/hooks/usePrompt";
import { useSlug } from "@/hooks/useSlug";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { usePollQueries } from "@/queries/polls/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import { CommunityPostStatus } from "./types";

const FALLBACK_REACH: PostReachDto = {
  addressCount: 0,
  residentCount: 0,
};

type CommunityPostReachDetailsModalProps = ModalBaseProps & {
  postType: CommunityFeedItemTypeDto;
} & (
    | {
        status: CommunityPostStatus.JUST_SCHEDULED;
        reach: PostReachDto | MassPostReachDto | undefined;
        scheduledDate: string;
        postId?: string;
        onEditMessage?: never;
      }
    | {
        status: CommunityPostStatus.JUST_DELIVERED;
        reach: PostReachDto | MassPostReachDto | undefined;
        scheduledDate?: never;
        postId?: string;
        onEditMessage?: never;
      }
    | {
        status: CommunityPostStatus.SCHEDULED;
        reach?: never;
        scheduledDate?: never;
        postId: string;
        onEditMessage: () => void;
      }
    | {
        status: CommunityPostStatus.ARCHIVED;
        reach?: never;
        scheduledDate?: never;
        postId: string;
        onEditMessage?: never;
      }
    | {
        status: CommunityPostStatus.DELETED;
        reach?: never;
        scheduledDate?: never;
        postId: string;
        onEditMessage?: never;
      }
    | {
        status: CommunityPostStatus.DELIVERED;
        reach?: never;
        scheduledDate?: never;
        postId: string;
        onEditMessage?: never;
      }
  );

export function CommunityPostReachDetailsModal({
  postType,
  reach,
  scheduledDate,
  postId,
  status,
  isOpened,
  onOpenChange,
  onEditMessage,
}: CommunityPostReachDetailsModalProps): React.ReactNode {
  const [isProgressShown, setIsProgressShown] = useState(
    status === CommunityPostStatus.JUST_SCHEDULED || status === CommunityPostStatus.JUST_DELIVERED,
  );

  const { t, i18n } = useTranslation();
  const slug = useSlug();
  const postHog = usePostHog();
  const sessionUser = useSessionUser();

  const communityFeedQueries = useCommunityFeedQueries();
  const pollQueries = usePollQueries();
  const {
    data: messageDetails,
    isLoading: isLoadingMessageDetails,
    isError: isErrorMessageDetails,
    refetch: refetchMessageDetails,
  } = useQuery({
    ...communityFeedQueries.messageDetails(postId!),
    enabled: Boolean(postId) && isOpened && postType === "message",
  });
  const {
    data: messageReach,
    isLoading: isLoadingMessageReach,
    isError: isErrorMessageReach,
    refetch: refetchMessageReach,
  } = useQuery({
    ...communityFeedQueries.messageReach({ messageId: postId! }),
    enabled: Boolean(postId) && !reach && isOpened && postType === "message",
  });
  const {
    data: pollDetails,
    isLoading: isLoadingPollDetails,
    isError: isErrorPollDetails,
    refetch: refetchPollDetails,
  } = useQuery({
    ...pollQueries.details(postId!),
    enabled: Boolean(postId) && isOpened && postType === "poll",
  });
  const {
    data: pollReach,
    isLoading: isLoadingPollReach,
    isError: isErrorPollReach,
    refetch: refetchPollReach,
  } = useQuery({
    ...pollQueries.reach(postId!),
    enabled: Boolean(postId) && !reach && isOpened && postType === "poll",
  });

  useEffect(() => {
    setIsProgressShown(status === CommunityPostStatus.JUST_SCHEDULED || status === CommunityPostStatus.JUST_DELIVERED);
  }, [isOpened, reach, status]);

  const handleRefetch = () => {
    void refetchMessageDetails();
    void refetchMessageReach();
    void refetchPollDetails();
    void refetchPollReach();
  };

  const reachData = reach || messageReach || pollReach || FALLBACK_REACH;
  const isProjectDraft = sessionUser.project.activeState === "unknown";

  const isLoading = isLoadingMessageReach || isLoadingMessageDetails || isLoadingPollDetails || isLoadingPollReach;
  const isError = isErrorMessageReach || isErrorMessageDetails || isErrorPollDetails || isErrorPollReach;
  const amountProgressBaseDuration = status === CommunityPostStatus.JUST_SCHEDULED ? 2000 : 4000; // 2s for scheduling, 4s for delivering
  const amountProgressDuration = Math.min(amountProgressBaseDuration + reachData.residentCount * 20, 15000); // 20ms per resident, with a base of 4s, max 15s
  const postedAt = postType === "message" ? messageDetails?.postedAt : pollDetails?.postedAt;
  const scheduledAt = postType === "message" ? messageDetails?.scheduledAt : pollDetails?.scheduledAt;
  const archivedAt = postType === "message" ? messageDetails?.archivedAt : undefined;
  const deletedAt = postType === "message" ? messageDetails?.deletedAt : pollDetails?.deletedAt;
  const deletedBy = postType === "message" ? messageDetails?.deletedBy : undefined;

  const { title, icon, description } = useMemo(() => {
    let currTitle = "";
    let currDescription: ReactNode = "";
    let currIcon = <CheckIcon />;

    try {
      switch (status) {
        case CommunityPostStatus.SCHEDULED:
          currTitle = t("page.admin-create-post.reach-details-modal.title.scheduled");
          currDescription = (
            <Trans
              i18nKey="page.admin-create-post.reach-details-modal.description.scheduled"
              values={{
                dateTime: formatDate(i18n, "datetime", scheduledAt || ""),
              }}
            />
          );

          break;
        case CommunityPostStatus.JUST_SCHEDULED:
          currTitle = t("page.admin-create-post.reach-details-modal.title.scheduled");

          if (isProjectDraft) {
            currDescription = (
              <Trans
                i18nKey="page.admin-create-post.reach-details-modal.description.scheduled.draft-project"
                values={{
                  projectName: sessionUser.project.name,
                  dateTime: formatDate(i18n, "datetime", scheduledDate),
                }}
              />
            );
          } else {
            currDescription = (
              <Trans
                i18nKey="page.admin-create-post.reach-details-modal.description.scheduled"
                values={{ dateTime: formatDate(i18n, "datetime", scheduledDate) }}
              />
            );
          }
          break;

        case CommunityPostStatus.ARCHIVED:
          currTitle = t("page.admin-create-post.reach-details-modal.title.archived");
          currDescription = (
            <Trans
              i18nKey="page.admin-create-post.reach-details-modal.description.archived"
              values={{ dateTime: formatDate(i18n, "datetime", archivedAt || "") }}
            />
          );
          currIcon = <ArchiveIcon />;
          break;

        case CommunityPostStatus.DELETED:
          currTitle = t("page.admin-create-post.reach-details-modal.title.deleted");
          currDescription = (
            <Trans
              i18nKey="page.admin-create-post.reach-details-modal.description.deleted"
              values={{
                dateTime: formatDate(i18n, "datetime", deletedAt || ""),
                deletionUser: deletedBy?.fullName || "",
              }}
            />
          );
          currIcon = <Trash01Icon />;
          break;

        case CommunityPostStatus.DELIVERED:
        case CommunityPostStatus.JUST_DELIVERED:
        default:
          currTitle = t("page.admin-create-post.reach-details-modal.title.delivered");

          if (isProjectDraft) {
            currDescription = (
              <Trans
                i18nKey="page.admin-create-post.reach-details-modal.description.delivered.draft-project"
                values={{
                  projectName: sessionUser.project.name,
                }}
              />
            );
          } else {
            currDescription = t("page.admin-create-post.reach-details-modal.description.delivered");
          }

          break;
      }

      return {
        title: currTitle,
        description: currDescription,
        icon: currIcon,
      };
    } catch {
      return {
        title: currTitle,
        description: currDescription,
        icon: currIcon,
      };
    }
  }, [
    status,
    t,
    i18n,
    scheduledAt,
    isProjectDraft,
    archivedAt,
    deletedAt,
    deletedBy?.fullName,
    sessionUser.project.name,
    scheduledDate,
  ]);

  return (
    <Modal.Root
      showClose={false}
      shouldCloseOnClickOutside={!isProgressShown}
      shouldCloseOnEsc={!isProgressShown}
      {...{ isOpened, onOpenChange }}
    >
      <AnimatePresence>
        {isProgressShown && (
          <ProgressView
            duration={amountProgressDuration}
            status={status}
            shouldStart={Boolean(reach)}
            onCompleted={() => setIsProgressShown(false)}
          />
        )}
      </AnimatePresence>
      <Modal.Body>
        <section className="relative flex flex-col gap-space-sm">
          {/* Loading & Error states */}
          <AnimatePresence>
            {(isLoading || isError || isProgressShown) && (
              <motion.div
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 flex items-center justify-center bg-background-default"
              >
                {isLoading && <FullSizeLoader noDelay />}
                {isError && <NoData onReload={handleRefetch} />}
                {isProgressShown && <ProgressBar progress={20} />}
              </motion.div>
            )}
          </AnimatePresence>
          {/* Success state */}
          <section className="flex flex-col items-center gap-space-md py-space-lg">
            {/* Icon */}
            <figure
              className={twResolve(
                "text-text-secondary",
                "overflow-hidden rounded-full p-space-sm",
                (status === CommunityPostStatus.DELIVERED ||
                  status === CommunityPostStatus.SCHEDULED ||
                  status === CommunityPostStatus.JUST_SCHEDULED ||
                  status === CommunityPostStatus.JUST_DELIVERED) &&
                  "bg-background-success text-text-default-inverse",
                status === CommunityPostStatus.ARCHIVED && "bg-background-secondary",
                status === CommunityPostStatus.DELETED && "bg-background-danger-secondary text-text-danger-secondary",
              )}
            >
              {cloneElement(icon, {
                size: 40,
              })}
            </figure>
            {/* Header */}
            <header className="text-center">
              <h1 className="text-heading-1">{title}</h1>
              <div className="mx-auto max-w-measure-reading">
                <MultiLineContent>{description}</MultiLineContent>
              </div>
            </header>
          </section>
          {/* Reach statistics */}
          {!isProjectDraft && (
            <Stack gap="md">
              <section className="flex flex-col items-center gap-space-md bg-background-secondary py-space-lg">
                <ul className="flex w-full flex-wrap items-center justify-center gap-y-2">
                  {"projectCount" in reachData && typeof reachData["projectCount"] === "number" && (
                    <li className="flex flex-1 flex-col items-center">
                      <Building04Icon size={24} />
                      <h2 className="text-heading-1">{reachData["projectCount"]}</h2>
                      <span className="text-body">
                        {t("common.entity.project", {
                          count: reachData["projectCount"],
                        })}
                      </span>
                    </li>
                  )}
                  {"addressCount" in reachData && typeof reachData["addressCount"] === "number" && (
                    <li className="flex flex-1 flex-col items-center">
                      <Building01Icon size={24} />
                      <h2 className="text-heading-1">{reachData["addressCount"]}</h2>
                      <span className="text-body">
                        {t("common.entity.address", {
                          count: reachData["addressCount"],
                        })}
                      </span>
                    </li>
                  )}
                  {"residentCount" in reachData && typeof reachData["residentCount"] === "number" && (
                    <li className="flex flex-1 flex-col items-center">
                      <Users01Icon size={24} />
                      <h2 className="text-heading-1">{reachData["residentCount"]}</h2>
                      <span className="text-body">
                        {sessionUser.project.type === "addressBased"
                          ? t("common.entity.resident", {
                              count: reachData["residentCount"],
                            })
                          : t("common.entity.people", {
                              count: reachData["residentCount"],
                            })}
                      </span>
                    </li>
                  )}
                </ul>
                {status === CommunityPostStatus.DELIVERED && postedAt && (
                  <Inline gap="xs" align="center" wrap={false}>
                    <span className="flex h-icon-control items-center">
                      <InfoCircleIcon tone="secondary" />
                    </span>
                    <span className="text-caption text-text-secondary">
                      {t("page.admin-create-post.reach-details-modal.disclaimer.statistics-captured-date", {
                        dateTime: formatDate(i18n, "date", postedAt),
                      })}
                    </span>
                  </Inline>
                )}
                {status === CommunityPostStatus.SCHEDULED && (
                  <Inline gap="xs" align="center" wrap={false}>
                    <span className="flex h-icon-control items-center">
                      <InfoCircleIcon tone="secondary" />
                    </span>
                    <span className="text-caption text-text-secondary">
                      {t("page.admin-create-post.reach-details-modal.disclaimer.statistics-live-until-delivery")}
                    </span>
                  </Inline>
                )}
              </section>

              <footer className="text-center text-caption text-text-secondary">
                <MultiLineContent>{t("page.admin-create-post.reach-details-modal.disclaimer")}</MultiLineContent>
              </footer>
            </Stack>
          )}
        </section>
      </Modal.Body>
      <Modal.Actions>
        {(status === CommunityPostStatus.JUST_DELIVERED || status === CommunityPostStatus.JUST_SCHEDULED) && (
          <>
            <Modal.Close>
              <Button variant="outline">{t("page.admin-create-post.success-modal.button.another-message")}</Button>
            </Modal.Close>
            <Button
              variant="filled"
              type="link"
              onClick={() => {
                postHog.capture("clicked_to_comm_feed_after_create_post");
              }}
              href={
                routes.communityFeed.list({ slug }) +
                `?${new URLSearchParams({
                  tab: status === CommunityPostStatus.JUST_SCHEDULED ? "scheduled" : "announcements",
                } satisfies MessageRouteParams["list"]).toString()}`
              }
              state={
                postId
                  ? ({
                      newMessageId: postId,
                    } satisfies MessageRouteStates["list"])
                  : undefined
              }
            >
              {t("page.admin-create-post.success-modal.button.done")}
            </Button>
          </>
        )}
        {status === CommunityPostStatus.SCHEDULED && (
          <>
            <Modal.Close>
              <Button variant="outline" onClick={onEditMessage}>
                {t("component.community-post.reach-details-modal.btn.edit-message")}
              </Button>
            </Modal.Close>
            <Modal.Close>
              <Button variant="filled">{t("common.action.close")}</Button>
            </Modal.Close>
          </>
        )}
        {(status === CommunityPostStatus.DELIVERED ||
          status === CommunityPostStatus.ARCHIVED ||
          status === CommunityPostStatus.DELETED) && (
          <Modal.Close>
            <Button variant="filled">{t("common.action.close")}</Button>
          </Modal.Close>
        )}
      </Modal.Actions>
    </Modal.Root>
  );
}

type ProgressViewProps = {
  duration: number;
  shouldStart: boolean;
  status: CommunityPostStatus;
  onCompleted: () => void;
};

function ProgressView({ duration, status, shouldStart, onCompleted }: ProgressViewProps) {
  // Set an semi-randomized initial progress to avoid stucking on 0% when `shouldStart` is false
  // oxlint-disable-next-line react-hooks/purity
  const initialProgress = Math.ceil(Math.random() * 5);
  const [progress, setProgress] = useState(initialProgress);

  const { t } = useTranslation();
  const intervalProgress = useRef<ReturnType<typeof setInterval>>(undefined);

  usePrompt(t("component.prompt.confirmation"), progress > 0 && progress < 100);

  useEffect(() => {
    if (!shouldStart) {
      return;
    }

    const progressInterval = duration / (100 - initialProgress);

    intervalProgress.current = setInterval(() => {
      setProgress((prev) => (prev < 100 ? prev + 1 : prev));
    }, progressInterval);

    return () => {
      if (intervalProgress.current) {
        clearInterval(intervalProgress.current);
      }
    };
  }, [duration, initialProgress, shouldStart]);

  useEffect(() => {
    if (progress === 100) {
      if (intervalProgress.current) {
        clearInterval(intervalProgress.current);
      }

      onCompleted();
    }
  }, [onCompleted, progress]);

  const formatDeliveryStageLabel = (stage: string, isDone: boolean) => {
    return isDone ? `✅ ${stage}` : `⏳ ${stage}`;
  };

  const labelProgressPrefix = t("component.community-post.reach-details-modal.label.delivering");

  return (
    <motion.div
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-10 flex items-center justify-center bg-background-default p-space-xl"
    >
      <section className="flex w-full flex-col gap-space-xs">
        <Spread>
          {status === CommunityPostStatus.JUST_SCHEDULED && (
            <span className="text-caption">{t("component.community-post.reach-details-modal.label.scheduling")}</span>
          )}
          {status === CommunityPostStatus.JUST_DELIVERED && (
            <span className="flex flex-wrap items-center gap-space-xs text-caption">
              {labelProgressPrefix}
              <span className={twResolve("text-text-secondary", progress >= 30 && "text-text-primary")}>
                {`${formatDeliveryStageLabel(t("common.label.app"), progress >= 30)}, `}
              </span>
              <span className={twResolve("text-text-secondary", progress >= 60 && "text-text-primary")}>
                {`${formatDeliveryStageLabel(t("common.label.web"), progress >= 60)}, `}
              </span>
              <span className={twResolve("text-text-secondary", progress >= 90 && "text-text-primary")}>
                {formatDeliveryStageLabel(t("common.label.email"), progress >= 90)}
              </span>
            </span>
          )}
          <span className="text-overline text-text-secondary">{`${progress}%`}</span>
        </Spread>
        <ProgressBar progress={progress} />
      </section>
    </motion.div>
  );
}
